import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import type { PriceTuple } from '../state/types';
import { fetchModelDetail } from '../api/client';
import { calculateTotal, type OptionRecord } from '../pricing/PriceCalculator';
import { formatCurrency } from '../pricing/PriceFormatter';
import { parseSettings } from '../config/BuilderConfig';

/**
 * Lightweight shapes for price line items.
 */
interface EngineData {
    id: number;
    engine_name: string;
    msrp_price: number;
    sale_price: number;
    dealer_net: number;
}

interface OptionData {
    id: number;
    option_name: string;
    option_code: string;
    msrp_price: number;
    sale_price: number;
    dealer_net: number;
}

interface OptionSection {
    options: OptionData[];
}

interface ModelData {
    model_name: string;
    model_year: number;
    base_msrp: number;
    base_sale_price: number;
    base_dealer_net: number;
    engines: EngineData[];
    option_sections: OptionSection[];
}

/**
 * Step 7 – Quote Summary: shows a complete price breakdown of all
 * selected components and the running total.
 */
export class StepQuoteSummary implements BuilderStep {
    readonly id = 'quote-summary';
    readonly label = 'Summary';

    private container: HTMLElement | null = null;

    constructor(
        private store: BuilderStore,
        private config: BootstrapConfig
    ) { }

    isEnabled(_config: BootstrapConfig): boolean {
        return true;
    }

    async render(container: HTMLElement): Promise<void> {
        this.container = container;
        const state = this.store.getState();
        const modelId = state.selectedModelId;

        if (!modelId) {
            container.innerHTML = `<div class="vc-step"><p class="vc-warning">No model selected.</p></div>`;
            return;
        }

        container.innerHTML = `
      <div class="vc-step vc-step-summary">
        <h2>Quote Summary</h2>
        <div id="vc-summary-body" class="vc-summary-body">
          <p class="vc-loading">Calculating...</p>
        </div>
      </div>
    `;

        try {
            const detail = (await fetchModelDetail(modelId)) as ModelData;
            this.renderSummary(detail);
        } catch {
            const body = container.querySelector('#vc-summary-body');
            if (body) body.innerHTML = `<p class="vc-error">Failed to load summary.</p>`;
        }
    }

    validate(): boolean {
        return true;
    }

    onEnter(): void { }

    onLeave(): void { }

    destroy(): void {
        this.container = null;
    }

    // ── private ─────────────────────────────────────────────

    private renderSummary(model: ModelData): void {
        const body = this.container?.querySelector('#vc-summary-body');
        if (!body) return;

        const state = this.store.getState();
        const settings = parseSettings(this.config);
        const { locale, currency, showSalePrice, showDealerNet } = settings;
        const isDealer = state.isDealer;

        // ── Base price ───────────────────────────────────────
        const basePrice: PriceTuple = {
            msrp: model.base_msrp,
            salePrice: model.base_sale_price,
            dealerNet: model.base_dealer_net,
        };

        // ── Engine price ────────────────────────────────────
        const engine = model.engines?.find(e => e.id === state.selectedEngineId);
        const enginePrice: PriceTuple = engine
            ? { msrp: engine.msrp_price, salePrice: engine.sale_price, dealerNet: engine.dealer_net }
            : { msrp: 0, salePrice: 0, dealerNet: 0 };

        // ── Color price ─────────────────────────────────────
        const boatColor = state.selectedColors.boatColor;
        const colorPrice: PriceTuple = boatColor
            ? { msrp: boatColor.msrpPrice, salePrice: boatColor.msrpPrice, dealerNet: boatColor.msrpPrice }
            : { msrp: 0, salePrice: 0, dealerNet: 0 };

        // ── Selected options ────────────────────────────────
        const allOptions: OptionData[] = (model.option_sections ?? []).flatMap(s => s.options);
        const selectedOpts: OptionRecord[] = allOptions
            .filter(o => state.selectedOptions.includes(o.id))
            .map(o => ({
                id: o.id,
                msrp: o.msrp_price,
                salePrice: o.sale_price,
                dealerNet: o.dealer_net,
            }));

        // ── Calculate total ─────────────────────────────────
        const total = calculateTotal(basePrice, enginePrice, colorPrice, selectedOpts, {
            exchangeRate: settings.exchangeRate,
            dealerBaseMargin: 0,
            dealerOptionsMargin: 0,
        });

        // Persist totals to store so StepCustomerInfo can include them in submission
        this.store.setState({ quoteTotals: total });

        // ── Price column header helper ──────────────────────
        const priceHeaders = (): string => {
            let h = '<th class="vc-price-col">MSRP</th>';
            if (showSalePrice) h += '<th class="vc-price-col">Sale</th>';
            if (isDealer && showDealerNet) h += '<th class="vc-price-col">Dealer Net</th>';
            return h;
        };

        const priceCells = (p: PriceTuple): string => {
            let c = `<td class="vc-price-col">${formatCurrency(p.msrp, locale, currency)}</td>`;
            if (showSalePrice) c += `<td class="vc-price-col">${formatCurrency(p.salePrice, locale, currency)}</td>`;
            if (isDealer && showDealerNet) c += `<td class="vc-price-col">${formatCurrency(p.dealerNet, locale, currency)}</td>`;
            return c;
        };

        // ── Build selected options rows ─────────────────────
        const selectedOptionNames = allOptions.filter(o => state.selectedOptions.includes(o.id));
        const optionRows = selectedOptionNames
            .map(
                o => `
      <tr>
        <td>${o.option_name}${o.option_code ? ` <small>(${o.option_code})</small>` : ''}</td>
        ${priceCells({ msrp: o.msrp_price, salePrice: o.sale_price, dealerNet: o.dealer_net })}
      </tr>
    `
            )
            .join('');

        body.innerHTML = `
      <table class="vc-summary-table">
        <thead>
          <tr>
            <th>Item</th>
            ${priceHeaders()}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>${model.model_name}</strong> (${model.model_year}) — Base</td>
            ${priceCells(basePrice)}
          </tr>
          ${engine
                ? `
          <tr>
            <td>Engine: ${engine.engine_name}</td>
            ${priceCells(enginePrice)}
          </tr>
          `
                : ''
            }
          ${boatColor
                ? `
          <tr>
            <td>Color: ${boatColor.name}</td>
            ${priceCells(colorPrice)}
          </tr>
          `
                : ''
            }
          ${optionRows}
        </tbody>
        <tfoot>
          <tr class="vc-total-row">
            <td><strong>Total</strong></td>
            ${priceCells(total)}
          </tr>
        </tfoot>
      </table>
      ${settings.disclaimer ? `<p class="vc-disclaimer">${settings.disclaimer}</p>` : ''}
    `;
    }
}
